import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, ArrowUp } from 'lucide-react';
import Lottie from 'lottie-react';
import '../../styles/contests/ListeningMaster.css';
import mixpanel from '../../utils/mixpanel'
import { API_URL } from '../../app/constants'
import Mina from '../../assets/images/mina.png';
import HiraganaLoader from '../../components/HiraganaLoader'
// Static import instead of dynamic import for better performance
import { 
  getListeningGroups, 
  getGroupChallenges, 
  validateListeningTranscript, 
  getListeningChatStream,
  completeListeningChallenge 
} from '../../api/contests';

// Helper function to convert video URLs to embed format
const formatVideoUrl = (url) => {
  if (!url) return '';
  
  try {
    // Handle YouTube Shorts URLs
    if (url.includes('youtube.com/shorts/')) {
      const videoId = url.split('youtube.com/shorts/')[1]?.split('?')[0];
      if (videoId) {
        return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`;
      }
    }
    
    // Handle youtube.com URLs
    if (url.includes('youtube.com/watch')) {
      const urlObj = new URL(url);
      const videoId = urlObj.searchParams.get('v');
      if (videoId) {
        let embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}`;
        const params = new URLSearchParams();
        
        // Add default parameters to avoid consent issues
        params.set('rel', '0');
        params.set('modestbranding', '1');
        
        // Handle start time (t or start parameter)
        const tParam = urlObj.searchParams.get('t');
        const startParam = urlObj.searchParams.get('start');
        if (tParam) {
          const startSeconds = tParam.replace(/[^\d]/g, ''); // Remove 's' and other chars
          if (startSeconds) params.set('start', startSeconds);
        } else if (startParam) {
          params.set('start', startParam);
        }
        
        // Handle end time
        const endParam = urlObj.searchParams.get('end');
        if (endParam) {
          params.set('end', endParam);
        }
        
        embedUrl += '?' + params.toString();
        return embedUrl;
      }
    }
    
    // Handle youtu.be URLs
    if (url.includes('youtu.be')) {
      const urlParts = url.split('youtu.be/')[1];
      const videoId = urlParts?.split('?')[0];
      if (videoId) {
        let embedUrl = `https://www.youtube.com/embed/${videoId}`;
        
        // Check for query parameters after the video ID
        if (urlParts.includes('?')) {
          const urlObj = new URL(`https://youtu.be/${urlParts}`);
          const params = new URLSearchParams();
          
          // Handle start time (t or start parameter)
          const tParam = urlObj.searchParams.get('t');
          const startParam = urlObj.searchParams.get('start');
          if (tParam) {
            const startSeconds = tParam.replace(/[^\d]/g, '');
            if (startSeconds) params.set('start', startSeconds);
          } else if (startParam) {
            params.set('start', startParam);
          }
          
          // Handle end time
          const endParam = urlObj.searchParams.get('end');
          if (endParam) {
            params.set('end', endParam);
          }
          
          if (params.toString()) {
            embedUrl += '?' + params.toString();
          }
        }
        return embedUrl;
      }
    }
    
    // Handle Vimeo URLs - convert to player.vimeo.com URLs
    if (url.includes('vimeo.com') && !url.includes('player.vimeo.com')) {
      // Extract the video ID
      let videoId = '';
      const urlWithoutParams = url.split('?')[0];
      
      // Match the ID pattern from the URL
      const match = urlWithoutParams.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
      if (match) {
        videoId = match[1];
        // If there's a hash after the ID, convert to h parameter
        const hash = match[2];
        // Pro account parameters that hide UI elements but keep play button
        const enhancedParams = 'title=0&byline=0&portrait=0&sidedock=0&color=9334EA&autopause=0&dnt=1&quality=1080p';
        
        if (hash) {
          return `https://player.vimeo.com/video/${videoId}?h=${hash}&${enhancedParams}`;
        }
        console.log(`Converting Vimeo URL: ${url} to player format with ID: ${videoId}`);
        return `https://player.vimeo.com/video/${videoId}?${enhancedParams}`;
      } else {
        // Fallback to return unmodified URL if the regex doesn't match
        console.warn(`Could not extract Vimeo ID from URL: ${url}. Using as-is.`);
        // Try direct player URL conversion
        // Pro account parameters that hide UI elements but keep play button
        const enhancedParams = 'title=0&byline=0&portrait=0&sidedock=0&color=9334EA&autopause=0&dnt=1&quality=1080p';
        return `${url.replace('vimeo.com/', 'player.vimeo.com/video/')}?${enhancedParams}`;
      }
    }
    
    // If already in embed format or unrecognized, return as is
    return url;
  } catch (e) {
    console.error('Error formatting video URL:', e);
    return url;
  }
};

const ListeningChallenge = () => {
  const navigate = useNavigate();
  const { groupId, challengeIndex, setId } = useParams();
  const currentIndex = parseInt(challengeIndex) || 0;
  
  // Determine if this is a user set or regular group
  const isUserSet = !!setId;
  const actualId = isUserSet ? setId : groupId;
  
  // States for all data and UI 
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contestResults, setContestResults] = useState({
    correctAnswers: 0,
    totalRounds: 0 
  });
  
  // Challenge state
  const [userAnswer, setUserAnswer] = useState('');
  const [hasPlayed, setHasPlayed] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [needsQuestions, setNeedsQuestions] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [animationData, setAnimationData] = useState(null);
  const [isReplaying, setIsReplaying] = useState(false);
  const [isTyping] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Video dragging state for mobile
  const [videoDragging, setVideoDragging] = useState(false);
  const [videoPosition, setVideoPosition] = useState({ x: 0, y: 0 });
  
  // Chat UI references and state
  const videoRef = useRef(null);
  const chatContentRef = useRef(null);
  const inputRef = useRef(null);
  const [messages, setMessages] = useState([]);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Video drag handlers for mobile - IMPROVED DRAG BAR
  const handleDragStart = (e) => {
    if (!isMobile) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const currentDragStartPos = { 
      x: clientX - videoPosition.x, 
      y: clientY - videoPosition.y 
    };
    
    setVideoDragging(true);
    
    // Add document-level event listeners for smooth dragging
    const moveHandler = (moveEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      
      const moveClientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveClientY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;
      
      const newX = moveClientX - currentDragStartPos.x;
      const newY = moveClientY - currentDragStartPos.y;
      
      setVideoPosition({ 
        x: newX, 
        y: newY 
      });
    };
    
    const endHandler = () => {
      setVideoDragging(false);
      document.removeEventListener('touchmove', moveHandler);
      document.removeEventListener('touchend', endHandler);
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', endHandler);
    };
    
    // Add listeners
    if (e.touches) {
      document.addEventListener('touchmove', moveHandler, { passive: false });
      document.addEventListener('touchend', endHandler, { passive: false });
    } else {
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', endHandler);
    }
  };
  
  const handleDragMove = (e) => {
    // Handled by document listeners now
    return;
  };
  
  const handleDragEnd = () => {
    // Handled by document listeners now
    return;
  };
  


  // Load group and challenges data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        let fetchedChallenges = [];
        
        if (isUserSet) {
          // Handle user listening sets
          const jwt = localStorage.getItem('jwt');
          const response = await fetch(`${API_URL}/api/v1/panel/listening-sets/${actualId}`, {
            headers: { 'Authorization': `Bearer ${jwt}` }
          });
          
          if (!response.ok) {
            throw new Error(`User set with ID ${actualId} not found`);
          }
          
          const userSet = await response.json();
          
          // Convert user set to challenges format
          fetchedChallenges = userSet.youtube_links.map((link, index) => ({
            id: `${userSet.set_id}-${index}`,
            videoUrl: link,
            video_url: link, // Keep both formats for compatibility
            youtube_url: link,
            youtubeUrl: link, // For the questions system
            title: `${userSet.set_name} - Video ${index + 1}`,
            description: `Video ${index + 1} from ${userSet.set_name}`,
            challenge_id: `${userSet.set_id}-${index}`
          }));
          
        } else {
          // Handle regular groups (existing logic)
          const groups = await getListeningGroups();
          const selectedGroup = groups.find(g => g.id === parseInt(groupId) || g.id === groupId);
          
          if (!selectedGroup) {
            throw new Error(`Group with ID ${groupId} not found`);
          }
          
          fetchedChallenges = await getGroupChallenges(groupId);
        }
        
        setChallenges(fetchedChallenges);
        
        // Update contest results
        setContestResults(prev => ({
          ...prev,
          totalRounds: fetchedChallenges.length
        }));
        
        // If challenge index is out of bounds, redirect to the first challenge
        if (currentIndex >= fetchedChallenges.length) {
          const redirectPath = isUserSet ? `/contests/user-set/${actualId}/0` : `/contests/challenge/${groupId}/0`;
          navigate(redirectPath);
        }
        
        // Track view in Mixpanel
        mixpanel.track('Listening Challenge Viewed', {
          group_id: isUserSet ? `user-set-${actualId}` : groupId,
          challenge_index: currentIndex,
          challenge_count: fetchedChallenges.length,
          is_user_set: isUserSet
        });
      } catch (error) {
        console.error('Error loading challenge data:', error);
        setError(`Error loading challenge: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [groupId, setId, currentIndex, navigate, isUserSet, actualId]);
  
  // Function to render markdown formatting in messages
  const renderMessageContent = (content) => {
    if (typeof content !== 'string') return content;
    
    // Simple detection: if there are A, B, C questions AND 1, 2, 3, 4 options, it's multiple choice
    const hasABCQuestions = content.includes('A.') && content.includes('B.') && content.includes('C.');
    const hasNumberedOptions = content.includes('1.') && content.includes('2.') && content.includes('3.') && content.includes('4.');
    const hasMultipleChoice = hasABCQuestions && hasNumberedOptions;
    
    if (hasMultipleChoice) {
      return renderMultipleChoiceContent(content);
    }
    
    return content.split('\n').map((line, lineIndex) => {
      // Handle bullet points (lines starting with *)
      if (line.trim().startsWith('* ')) {
        const bulletContent = line.trim().substring(2); // Remove '* '
        return (
          <li key={lineIndex} style={{ marginLeft: '20px', listStyleType: 'disc' }}>
            {renderInlineFormatting(bulletContent)}
          </li>
        );
      }
      
      // Handle regular lines
      return (
        <p key={lineIndex}>
          {renderInlineFormatting(line)}
        </p>
      );
    });
  };

  // Function to render multiple choice questions with clickable boxes
  const renderMultipleChoiceContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentQuestion = null;
    let options = [];
    let inQuestionMode = false;
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) return;
      
      // Check if this is a question (starts with A. B. or C.)
      if (trimmedLine.match(/^[ABC]\.\s/)) {
        // If we have a previous question with options, render it
        if (currentQuestion && options.length >= 4) {
          elements.push(renderQuestionWithOptions(currentQuestion, options, elements.length));
          options = [];
        }
        currentQuestion = trimmedLine;
        inQuestionMode = true;
        return;
      }
      
      // Check if this is an option (1. 2. 3. 4.)
      const optionMatch = trimmedLine.match(/^([1-4])\.\s*(.+)/);
      if (optionMatch && inQuestionMode) {
        options.push(trimmedLine);
        
        // If we have 4 options, complete this question
        if (options.length === 4) {
          elements.push(renderQuestionWithOptions(currentQuestion, options, elements.length));
          currentQuestion = null;
          options = [];
          inQuestionMode = false;
        }
        return;
      }
      
      // If we encounter non-option text after starting a question, finish the current question if we have enough options
      if (inQuestionMode && currentQuestion && options.length >= 4) {
        elements.push(renderQuestionWithOptions(currentQuestion, options, elements.length));
        currentQuestion = null;
        options = [];
        inQuestionMode = false;
      }
      
      // Regular text - only add if not part of a question/option sequence
      if (!inQuestionMode && trimmedLine) {
        elements.push(
          <p key={`text-${index}`} style={{ margin: '0.5em 0' }}>
            {renderInlineFormatting(trimmedLine)}
          </p>
        );
      }
    });
    
    // Handle the last question if exists
    if (currentQuestion && options.length > 0) {
      elements.push(renderQuestionWithOptions(currentQuestion, options, elements.length));
    }
    
    return <div>{elements}</div>;
  };

  // Function to render a single question with clickable option boxes
  const renderQuestionWithOptions = (question, options, questionIndex) => {
    // Extract question letter from question text (A. B. or C.)
    const questionLetter = question.match(/^([ABC])\./)?.[1] || 'A';
    
    return (
      <div key={`question-${questionIndex}`} style={{ 
        margin: '20px 0', 
        padding: '15px', 
        border: '1px solid #e5e7eb', 
        borderRadius: '8px',
        backgroundColor: '#f9fafb'
      }}>
        <p style={{ fontWeight: '600', marginBottom: '12px', color: '#1f2937', fontFamily: 'Manrope, sans-serif', fontSize: '15px' }}>
          {renderInlineFormatting(question)}
        </p>
        
        <div style={{ display: 'grid', gap: '8px' }}>
          {options.map((option, optionIndex) => {
            const optionNumber = option.charAt(0); // 1, 2, 3, or 4
            const optionText = option.substring(2).trim(); // Remove "1. " part
            const isSelected = selectedAnswers[questionLetter]?.option === optionNumber;
            
            return (
              <button
                key={`option-${questionIndex}-${optionIndex}`}
                onClick={() => handleOptionClick(questionLetter, optionNumber, optionText)}
                disabled={answersSubmitted}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  border: `2px solid ${isSelected ? '#9333ea' : '#d1d5db'}`,
                  borderRadius: '8px',
                  backgroundColor: isSelected ? '#f3f4f6' : 'white',
                  cursor: answersSubmitted ? 'not-allowed' : 'pointer',
                  opacity: answersSubmitted ? 0.7 : 1,
                  transition: 'all 0.2s',
                  textAlign: 'left'
                }}
              >
                <span style={{
                  minWidth: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#9333ea',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  marginRight: '12px',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '15px'
                }}>
                  {optionNumber}
                </span>
                <span style={{ flex: 1, fontSize: '15px', lineHeight: '1.4', fontFamily: 'Manrope, sans-serif' }}>
                  {renderInlineFormatting(optionText)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // State to track selected answers
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [answersSubmitted, setAnswersSubmitted] = useState(false);

  // Handle option click
  const handleOptionClick = (questionLetter, optionNumber, optionText) => {
    // Don't allow clicking if answers already submitted
    if (answersSubmitted) return;
    
    // Store the selected answer (this will replace any previous selection for this question)
    setSelectedAnswers(prev => ({
      ...prev,
      [questionLetter]: { option: optionNumber, text: optionText }
    }));
    
    // Check if all questions are answered
    const updatedAnswers = { ...selectedAnswers, [questionLetter]: { option: optionNumber, text: optionText } };
    const answeredQuestions = Object.keys(updatedAnswers);
    
    // Check if all questions (A, B, C) are answered
    const allAnswered = answeredQuestions.length === 3 && answeredQuestions.includes('A') && answeredQuestions.includes('B') && answeredQuestions.includes('C');
    
    if (allAnswered) {
      // All questions answered, send to AI
      const answerMessage = `My answers:\nA: ${updatedAnswers.A.option}) ${updatedAnswers.A.text}\nB: ${updatedAnswers.B.option}) ${updatedAnswers.B.text}\nC: ${updatedAnswers.C.option}) ${updatedAnswers.C.text}`;
      
      // Add user's answers to chat
      setMessages(prev => [...prev, { 
        sender: 'user', 
        content: answerMessage 
      }]);
      
      // Send all answers to AI for feedback
      handleProceedToAnswerWithText(answerMessage);
      
      // Mark as submitted (don't reset selections)
      setAnswersSubmitted(true);
      setHasSubmitted(true); // Show Next button
    }
  };
  
  // Function to handle inline formatting (**bold**, *italic*)
  const renderInlineFormatting = (text) => {
    const parts = [];
    let currentIndex = 0;
    
    // Regular expression to find **bold** and *italic* patterns
    const formatRegex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let match;
    
    while ((match = formatRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }
      
      const matchedText = match[0];
      if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
        // Bold text
        const boldText = matchedText.slice(2, -2);
        parts.push(<strong key={`bold-${match.index}`}>{boldText}</strong>);
      } else if (matchedText.startsWith('*') && matchedText.endsWith('*')) {
        // Italic text
        const italicText = matchedText.slice(1, -1);
        parts.push(<em key={`italic-${match.index}`}>{italicText}</em>);
      }
      
      currentIndex = match.index + matchedText.length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };
  
  // Get current challenge
  const currentChallenge = challenges.length > currentIndex ? challenges[currentIndex] : null;

  // Function to send Mina's automatic first question
  const sendMinaAutoQuestion = useCallback(async () => {
    try {
      // Get YouTube URL from current challenge
      const youtubeUrl = currentChallenge?.youtubeUrl || '';
      
      if (!youtubeUrl) {
        console.warn('No YouTube URL found for this challenge, cannot send auto question');
        return;
      }

      // Check if this is the first video of the challenge group
      const isFirstVideo = currentIndex === 0;
      
      if (isFirstVideo) {
        // For the first video, show welcome message first
        setMessages([{
          sender: 'bot',
          content: 'こんにちは！Ready to get started? Listen to this first video, and then I\'ll quiz you a little. がんばってね！',
          id: Date.now()
        }]);
        setNeedsQuestions(true); // Mark that we need to send questions later
        return; // Don't send questions immediately for first video
      }

      // For subsequent videos, show appropriate welcome message first
      const videoNumber = currentIndex + 1;
      const totalVideos = challenges.length;
      let welcomeMessage = '';
      
      // Different messages for different videos (but not konnichiwa)
      const welcomeMessages = [
        `Ready for the next challenge? Video ${videoNumber} of ${totalVideos} - Listen carefully!`,
        `Great progress! Here's video ${videoNumber} of ${totalVideos} - Let's keep going!`,
        `Nice work so far! Video ${videoNumber} of ${totalVideos} - Time for another one!`,
        `You're doing well! Video ${videoNumber} of ${totalVideos} - Listen and I'll quiz you again!`
      ];
      
      // Pick message based on video index (cycling through the 4 messages)
      welcomeMessage = welcomeMessages[(currentIndex - 1) % 4];
      
      // Add welcome message first
      setMessages([{
        sender: 'bot',
        content: welcomeMessage,
        id: Date.now()
      }]);
      
      // Then add loading message and get questions after a short delay
      setTimeout(async () => {
        const tempId = Date.now();
        setMessages(prev => [...prev, {
          sender: 'bot',
          content: '...',
          id: tempId
        }]);

        // Send automatic question to AI (Mina will analyze the video and ask questions)
        const autoMessage = "I just watched the video. Can you ask me questions to test my understanding?";
        
        try {
          const response = await getListeningChatStream(
            currentChallenge?.id || currentChallenge?.challenge_id,
            autoMessage,
            youtubeUrl,
            [] // Empty conversation history for first question
          );
          
          // Process streaming response
          const reader = response.body.getReader();
          const decoder = new TextDecoder("utf-8");
          let fullResponse = '';
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            const chunk = decoder.decode(value);
            
            // Check if this chunk contains completion JSON
            const jsonStart = chunk.indexOf('{"completed":');
            if (jsonStart !== -1) {
              // Process text before JSON
              if (jsonStart > 0) {
                const textBeforeJson = chunk.substring(0, jsonStart);
                fullResponse += textBeforeJson;
              }
            } else {
              // Regular text chunk
              fullResponse += chunk;
            }
            
            // Update message in UI
            const cleanResponse = fullResponse.trim();
            if (cleanResponse) {
              setMessages(prev => prev.map(msg => 
                msg.id === tempId 
                  ? { ...msg, content: cleanResponse }
                  : msg
              ));
            }
          }
          
        } catch (error) {
          console.error("Error sending questions for subsequent video:", error);
          setMessages(prev => prev.map(msg => 
            msg.id === tempId 
              ? { ...msg, content: "I'm ready to help you understand this video! What did you think of it?" }
              : msg
          ));
        }
      }, 1500); // 1.5 second delay to let user read welcome message
      
    } catch (error) {
      console.error("Error in sendMinaAutoQuestion:", error);
      setMessages([{
        sender: 'bot',
        content: "I'm ready to help you understand this video! What did you think of it?",
        id: Date.now()
      }]);
    }
  }, [currentChallenge, currentIndex, challenges.length]);

  // Load confetti animation and play sound when completion screen shows
  useEffect(() => {
    if (showCompletion) {
      console.log('Loading completion animation...');
      
      // Play fanfare sound
      const audio = new Audio(process.env.PUBLIC_URL + '/sounds/fanfare_session_complete.mp3');
      audio.play().catch(error => console.error('Failed to play sound:', error));
      
      // Load confetti animation
      fetch(process.env.PUBLIC_URL + '/Animation - 1749657318004.json')
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          console.log('Completion animation data loaded:', data);
          setAnimationData(data);
        })
        .catch(error => console.error('Failed to load completion animation:', error));
    }
  }, [showCompletion]);

  // Check for replay state on component mount
  useEffect(() => {
    if (sessionStorage.getItem('isReplaying') === 'true') {
      setIsReplaying(true);
      // Clear the replay state after component loads
      setTimeout(() => {
        sessionStorage.removeItem('isReplaying');
        sessionStorage.removeItem('replayTarget');
        setIsReplaying(false);
      }, 1000);
    }
  }, []);

  // Function to send questions when user finishes watching the first video
  const sendQuestionsAfterWelcome = useCallback(async () => {
    try {
      const youtubeUrl = currentChallenge?.youtubeUrl || '';
      
      if (!youtubeUrl) {
        console.warn('No YouTube URL found for this challenge, cannot send questions');
        return;
      }

      // Add a temporary loading message
      const tempId = Date.now();
      setMessages(prev => [...prev, {
        sender: 'bot',
        content: '...',
        id: tempId
      }]);

      // Send automatic question to AI (Mina will analyze the video and ask questions)
      const autoMessage = "I just watched the video. Can you ask me questions to test my understanding?";
      
      const response = await getListeningChatStream(
        currentChallenge?.id || currentChallenge?.challenge_id,
        autoMessage,
        youtubeUrl,
        [] // Empty conversation history for first question
      );
      
      // Process streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullResponse = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        
        // Check if this chunk contains completion JSON
        const jsonStart = chunk.indexOf('{"completed":');
        if (jsonStart !== -1) {
          // Process text before JSON
          if (jsonStart > 0) {
            const textBeforeJson = chunk.substring(0, jsonStart);
            fullResponse += textBeforeJson;
          }
        } else {
          // Regular text chunk
          fullResponse += chunk;
        }
        
        // Update message in UI
        const cleanResponse = fullResponse.trim();
        if (cleanResponse) {
          setMessages(prev => prev.map(msg => 
            msg.id === tempId 
              ? { ...msg, content: cleanResponse }
              : msg
          ));
        }
      }
      
      setNeedsQuestions(false); // Questions have been sent
      
    } catch (error) {
      console.error("Error sending questions after welcome:", error);
      setMessages(prev => [...prev, {
        sender: 'bot',
        content: "I'm ready to help you understand this video! What did you think of it?",
        id: Date.now()
      }]);
      setNeedsQuestions(false);
    }
  }, [currentChallenge]);


  // Reset chat when challenge changes
  useEffect(() => {
    setMessages([]);
    setUserAnswer('');
    setHasSubmitted(false);
    setHasPlayed(false);
    setSelectedAnswers({}); // Reset selected answers
    setAnswersSubmitted(false); // Reset submission state
    setNeedsQuestions(false); // Reset questions state
  }, [currentIndex, groupId]);

  // Send Mina's first question when video is played
  useEffect(() => {
    if (hasPlayed && messages.length === 0 && currentChallenge) {
      // Send Mina's automatic first question
      sendMinaAutoQuestion();
    }
  }, [hasPlayed, currentChallenge, messages.length, sendMinaAutoQuestion]);

  // Send questions after welcome message for first video
  useEffect(() => {
    if (hasPlayed && needsQuestions && currentIndex === 0) {
      // Small delay to let user read the welcome message
      const timer = setTimeout(() => {
        sendQuestionsAfterWelcome();
      }, 2000); // 2 second delay
      
      return () => clearTimeout(timer);
    }
  }, [hasPlayed, needsQuestions, currentIndex, sendQuestionsAfterWelcome]);

  // Auto-scroll effect for chat messages
  useEffect(() => {
    const scrollToBottom = () => {
      if (chatContentRef.current) {
        chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
      }
    };

    scrollToBottom();
    
    // Also scroll on resize events
    const handleResize = () => {
      scrollToBottom();
      if (document.activeElement?.tagName === 'INPUT') {
        setTimeout(scrollToBottom, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [messages]);
  
  // Handle proceed to answer with custom text (for multiple choice)
  const handleProceedToAnswerWithText = async (answerText) => {
    // Clean the answer text
    const userAnswerClean = answerText.trim();
    
    // First add a temporary loading message
    const tempId = Date.now();
    setMessages(prev => [...prev, {
      sender: 'bot',
      content: '...',
      id: tempId
    }]);
    
    // Get challenge ID - handle both id and challenge_id conventions
    const challengeId = currentChallenge?.id || currentChallenge?.challenge_id;
    
    if (!challengeId) {
      console.error("Cannot find challenge ID:", currentChallenge);
      setMessages(prev => prev.map(msg => 
        msg.id === tempId 
          ? { ...msg, content: "Sorry, there was a problem identifying this challenge." }
          : msg
      ));
      return;
    }
    
    // Process the user's answer using backend validation and chat
    const processUserAnswer = async () => {
      try {
        console.log(`Processing answer for challenge ${challengeId}: "${userAnswerClean}"`);
        
        // Get YouTube URL from current challenge
        const youtubeUrl = currentChallenge?.youtubeUrl || '';
        
        if (!youtubeUrl) {
          console.warn('No YouTube URL found for this challenge, cannot use AI analysis');
          setMessages(prev => prev.map(msg => 
            msg.id === tempId 
              ? { ...msg, content: "I'm ready to help you understand this video! What did you think of it?" }
              : msg
          ));
          return;
        }
        
        // Build conversation history from current messages (excluding loading message)
        const conversationHistory = messages
          .filter(msg => msg.id !== tempId) // Exclude the loading message
          .map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.content
          }));
        
        // Track listening message sent
        mixpanel.track('Listening Message Sent', {
          challenge_id: challengeId,
          group_id: groupId,
          challenge_index: currentIndex,
          message_length: userAnswerClean.length,
          youtube_url: youtubeUrl,
          is_multiple_choice: true,
          conversation_turn: conversationHistory.length + 1
        });
        
        const response = await getListeningChatStream(challengeId, userAnswerClean, youtubeUrl, conversationHistory);
        
        // Process streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let fullResponse = '';
        
        console.log("Starting to read stream");
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log("Stream reading complete");
            break;
          }
          
          // Decode the chunk
          const chunk = decoder.decode(value);
          
          // Check if this chunk contains completion JSON
          const jsonStart = chunk.indexOf('{"completed":');
          
          if (jsonStart !== -1) {
            // Process text before JSON
            if (jsonStart > 0) {
              const textBeforeJson = chunk.substring(0, jsonStart);
              fullResponse += textBeforeJson;
            }
          } else {
            // Regular text chunk
            fullResponse += chunk;
          }
          
          // Update message in UI
          const cleanResponse = fullResponse.trim();
          if (cleanResponse) {
            setMessages(prev => prev.map(msg => 
              msg.id === tempId 
                ? { ...msg, content: cleanResponse } 
                : msg
            ));
          }
        }
        
        
      } catch (error) {
        console.error("Error processing answer:", error);
        
        // Show the error in UI for better debugging
        setMessages(prev => prev.map(msg => 
          msg.id === tempId 
            ? { ...msg, content: `There was an error processing your answer: ${error.message}. Please try again.` }
            : msg
        ));
      }
    };
    
    // Start processing
    processUserAnswer();
  };

  // Handle proceed to answer
  const handleProceedToAnswer = () => {
    if (!userAnswer.trim()) return;
    
    // Add user message to chat immediately
    setMessages(prev => [...prev, { sender: 'user', content: userAnswer }]);
    
    // Clean the user answer
    const userAnswerClean = userAnswer.trim();
    
    // Reset the input field
    setUserAnswer('');
    
    // First add a temporary loading message
    const tempId = Date.now();
    setMessages(prev => [...prev, {
      sender: 'bot',
      content: '...',
      id: tempId
    }]);
    
    // Get challenge ID - handle both id and challenge_id conventions
    const challengeId = currentChallenge?.id || currentChallenge?.challenge_id;
    
    if (!challengeId) {
      console.error("Cannot find challenge ID:", currentChallenge);
      setMessages(prev => prev.map(msg => 
        msg.id === tempId 
          ? { ...msg, content: "Sorry, there was a problem identifying this challenge." }
          : msg
      ));
      return;
    }
    
    // Process the user's answer using backend validation and chat
    const processUserAnswer = async () => {
      try {
        console.log(`Processing answer for challenge ${challengeId}: "${userAnswerClean}"`);
        
        // Using static imports (already available at the top of the file)
        
        // Check if user message already includes a transcript marker
        // Backend looks for "this is what I heard:" or "this is my transcript:"
        console.log("Original user message:", userAnswerClean);
        
        // IMPORTANT: The backend is looking for these exact strings with capital I not lowercase i
        let transcriptionMessage = userAnswerClean;
        if (!transcriptionMessage.toLowerCase().includes("this is what i heard:") && 
            !transcriptionMessage.toLowerCase().includes("this is my transcript:")) {
          // If not, add the marker - USE THE EXACT FORMAT the backend expects
          transcriptionMessage = `this is what I heard: ${transcriptionMessage}`;
          console.log("Added transcript marker with exact casing backend expects");
        }
        
        console.log("Final transcript message:", transcriptionMessage);
        
        // First, try to get streaming chat response
        console.log(`Sending chat request with message: "${transcriptionMessage}"`);
        
        // Get YouTube URL from current challenge
        const youtubeUrl = currentChallenge?.youtubeUrl || '';
        
        if (!youtubeUrl) {
          console.warn('No YouTube URL found for this challenge, cannot use AI analysis');
          // Handle fallback or show error message
          return;
        }
        
        // Build conversation history from current messages (excluding loading message)
        const conversationHistory = messages
          .filter(msg => msg.id !== tempId) // Exclude the loading message
          .map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.content
          }));
        
        // Track listening message sent
        mixpanel.track('Listening Message Sent', {
          challenge_id: challengeId,
          group_id: groupId,
          challenge_index: currentIndex,
          message_length: userAnswerClean.length,
          youtube_url: youtubeUrl,
          is_transcript_attempt: transcriptionMessage.toLowerCase().includes("this is what i heard:") || transcriptionMessage.toLowerCase().includes("this is my transcript:"),
          conversation_turn: conversationHistory.length + 1
        });
        
        const response = await getListeningChatStream(challengeId, transcriptionMessage, youtubeUrl, conversationHistory);
        
        // Process streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let fullResponse = '';
        let isCompleted = false;
        let similarityResult = 0;
        
        console.log("Starting to read stream");
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log("Stream reading complete");
            break;
          }
          
          // Decode the chunk
          const chunk = decoder.decode(value);
          console.log("Received chunk:", chunk.substring(0, 50) + (chunk.length > 50 ? "..." : ""));
          
          // Check if this chunk contains completion JSON
          const jsonStart = chunk.indexOf('{"completed":');
          
          if (jsonStart !== -1) {
            // Extract and parse JSON data
            const jsonData = chunk.substring(jsonStart);
            console.log("Found JSON completion data:", jsonData);
            
            try {
              const completionData = JSON.parse(jsonData);
              isCompleted = completionData.completed;
              similarityResult = completionData.similarity;
              console.log(`Completion status: ${isCompleted}, Similarity: ${similarityResult}%`);
              
              // Update contest results if completed
              if (isCompleted) {
                setHasSubmitted(true);
                setContestResults(prev => ({
                  ...prev,
                  correctAnswers: prev.correctAnswers + 1
                }));
              }
              
              // Process text before JSON
              if (jsonStart > 0) {
                const textBeforeJson = chunk.substring(0, jsonStart);
                fullResponse += textBeforeJson;
              }
            } catch (e) {
              console.error("Error parsing JSON completion data:", e);
              // Add the whole chunk as text if JSON parsing fails
              fullResponse += chunk;
            }
          } else {
            // Regular text chunk
            fullResponse += chunk;
          }
          
          // Update message in UI
          const cleanResponse = fullResponse.trim();
          if (cleanResponse) {
            setMessages(prev => prev.map(msg => 
              msg.id === tempId 
                ? { ...msg, content: cleanResponse } 
                : msg
            ));
          }
        }
        
        // If we received no text response or only whitespace,
        // check with the validate endpoint as fallback
        if (!fullResponse.trim()) {
          console.log("No text response received, using validate endpoint as fallback");
          const validationResult = await validateListeningTranscript(challengeId, userAnswerClean);
          
          // Update UI based on validation result
          const similarity = validationResult.similarity;
          const isCorrect = validationResult.isCorrect;
          
          console.log(`Validation result: similarity=${similarity}, isCorrect=${isCorrect}`);
          
          // Update message based on validation
          if (isCorrect) {
            setHasSubmitted(true);
            setContestResults(prev => ({
              ...prev,
              correctAnswers: prev.correctAnswers + 1
            }));
            
            const contestAnswer = currentChallenge?.answerText || currentChallenge?.answer_text;
            const contestTranslation = currentChallenge?.translation || "No translation available";
            
            setMessages(prev => prev.map(msg => 
              msg.id === tempId 
                ? { ...msg, content: `Great job! Your answer is correct! 🎉\n\nThe correct answer is: "${contestAnswer}" which means "${contestTranslation}".` }
                : msg
            ));
          } else {
            let feedbackMessage = '';
            if (similarity > 70) {
              feedbackMessage = `You're getting close! Your answer is about ${similarity}% correct. Listen carefully to the exact pronunciation.`;
            } else if (similarity > 40) {
              feedbackMessage = `You got some parts right. Your answer is about ${similarity}% correct. Focus on listening to each sound carefully.`;
            } else {
              feedbackMessage = `Keep practicing! Your answer was about ${similarity}% correct. Japanese listening takes time to master.`;
            }
            
            setMessages(prev => prev.map(msg => 
              msg.id === tempId
                ? { ...msg, content: feedbackMessage + "\n\nYou typed: \"" + userAnswerClean + "\"" } 
                : msg
            ));
          }
        }
        
        // Mark as submitted after processing
        setHasSubmitted(true);
        
      } catch (error) {
        console.error("Error processing answer:", error);
        
        // Show the error in UI for better debugging
        setMessages(prev => prev.map(msg => 
          msg.id === tempId 
            ? { ...msg, content: `There was an error processing your answer: ${error.message}. Please try again.` }
            : msg
        ));
      }
    };
    
    // Start processing
    processUserAnswer();
  };
  
  // Handle move to next challenge
  const handleNext = async () => {
    try {
      // If the current challenge was completed successfully, mark it as complete in the backend
      if (currentChallenge && hasSubmitted) {
        // Using static imports (already available at the top of the file)
        
        // Get challenge ID - handle both id and challenge_id conventions
        const challengeId = currentChallenge?.id || currentChallenge?.challenge_id;
        
        if (challengeId && !isUserSet) {
          // Record completion in the backend (only for regular challenges)
          try {
            console.log(`Marking challenge ${challengeId} as completed`);
            await completeListeningChallenge(challengeId);
            console.log(`Challenge ${challengeId} marked as completed`);
            
            // Track event in Mixpanel
            mixpanel.track('Listening Challenge Completed', {
              challenge_id: challengeId,
              challenge_index: currentIndex,
              group_id: groupId
            });
          } catch (error) {
            console.error(`Error marking challenge as completed:`, error);
            // Continue anyway - don't block progression due to recording error
          }
        }
      }
    } catch (error) {
      console.error("Error marking challenge as completed:", error);
    }
    
    // Move to the next challenge
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < challenges.length) {
      // Navigate to the next challenge
      const nextPath = isUserSet ? `/contests/user-set/${actualId}/${nextIndex}` : `/contests/challenge/${groupId}/${nextIndex}`;
      navigate(nextPath);
    } else {
      // Track contest completion in Mixpanel
      mixpanel.track('Listening Contest Group Completed', {
        challenge_count: challenges.length,
        correct_answers: contestResults.correctAnswers,
        group_id: groupId
      });
      
      // Stop video/audio before showing completion screen
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      
      // Stop any iframe videos by reloading them
      const iframes = document.querySelectorAll('.listening-master-video');
      iframes.forEach(iframe => {
        if (iframe.tagName === 'IFRAME') {
          const src = iframe.src;
          iframe.src = '';
          iframe.src = src;
        }
      });
      
      // Show completion screen
      setShowCompletion(true);
    }
  };
  
  // Handle completion screen action - go to dashboard
  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  // Handle replay challenge - reload the route completely
  const handleReplayChallenge = () => {
    // Store replay state in sessionStorage to persist across route changes
    sessionStorage.setItem('isReplaying', 'true');
    sessionStorage.setItem('replayTarget', `/contests/challenge/${groupId}/0`);
    
    // Show loader immediately
    setIsReplaying(true);
    
    // Force a complete reset by going to contests first, then to challenge
    navigate('/contests');
    
    // Add a small delay then navigate to first challenge
    setTimeout(() => {
      const firstChallengePath = isUserSet ? `/contests/user-set/${actualId}/0` : `/contests/challenge/${groupId}/0`;
      navigate(firstChallengePath);
    }, 100);
  };

  // Handle going back to groups
  const handleBackToGroups = () => {
    // Always show exit modal when clicking X button during a challenge
    // This prevents accidental exits and potential progress loss
    setShowExitModal(true);
  };
  
  // Loading state or replaying
  if (loading || isReplaying) {
    return (
      <div className="listening-master-loading">
        <HiraganaLoader />
        <p>Loading challenge...</p>
      </div>
    );
  }
  
  // Error state
  if (error || !currentChallenge) {
    return (
      <div className="listening-master-no-challenges">
        <h2>Error</h2>
        <p>{error || "Could not load this challenge"}</p>
        <button 
          className="listening-master-play-button"
          onClick={() => navigate('/contests')}
        >
          Back to Challenges
        </button>
      </div>
    );
  }

  return (
    <div className="question-layout-container">
      {/* Header */}
      <div className="question-layout-header">
        <div className="question-layout-progress">
          <span>{currentIndex + 1} of {challenges.length}</span>
        </div>
        <button
          className="question-layout-exit-btn"
          onClick={handleBackToGroups}
          aria-label="Back to groups"
        >
          <X size={20} />
        </button>
      </div>
      
      {/* Main split content */}
      <div className="question-layout-content">
        {/* Left side - Video */}
        <div 
          className="question-layout-left"
          style={isMobile ? {
            transform: `translate(${videoPosition.x}px, ${videoPosition.y}px)`,
            transition: videoDragging ? 'none' : 'transform 0.3s ease',
            userSelect: 'none'
          } : {}}
        >
          <div 
            className="listening-video-container"
            style={isMobile ? { position: 'relative' } : {}}
          >
            {/* Drag handle for mobile - TOP BAR */}
            {isMobile && (
              <div 
                className="video-drag-handle"
                style={{
                  position: 'absolute',
                  top: '0px',
                  left: '0px',
                  right: '0px',
                  height: '30px',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  cursor: videoDragging ? 'grabbing' : 'grab'
                }}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
              >
                ⋮⋮ Drag to move ⋮⋮
              </div>
            )}
            <div className="listening-video-wrapper">
            {(() => {
              // Enhanced debugging for video URLs
              const videoUrl = currentChallenge?.videoUrl || currentChallenge?.video_url;
              console.log("🎥 Original video URL:", videoUrl);
              
              // Check if this is a YouTube or Vimeo URL
              const isExternalVideo = videoUrl?.includes('youtube.com') || 
                                     videoUrl?.includes('youtu.be') ||
                                     videoUrl?.includes('vimeo.com');
                                     
              console.log("🎥 Is external video?", isExternalVideo);
              
              if (isExternalVideo) {
                const formattedUrl = formatVideoUrl(videoUrl);
                console.log("🎥 Formatted video URL:", formattedUrl);
                
                return (
                  <iframe
                    className="listening-master-video"
                    src={formattedUrl}
                    title="Listening practice"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    onLoad={() => {
                      console.log("🎥 iframe loaded");
                      // Automatically consider it played when iframe loads
                      setHasPlayed(true);
                    }}
                    onError={(e) => console.error("🎥 iframe loading error:", e)}
                  ></iframe>
                );
              } else {
                console.log("🎥 Using native video player with URL:", videoUrl);
                return (
                  <video 
                    ref={videoRef}
                    src={videoUrl}
                    className="listening-master-video"
                    playsInline
                    controls
                    onPlay={() => {
                      setHasPlayed(true);
                    }}
                    onEnded={() => console.log("Video playback ended")}
                  />
                );
              }
            })()}
            </div>
          </div>
        </div>
        
        {/* Right side - Chat */}
        <div className="question-layout-right">
          {/* Desktop: Original chat system */}
          {!isMobile && (
            <>
              <div className="listening-master-chat-content" ref={chatContentRef}>
                {messages.map((msg, index) => (
                  <div key={index} className={`message ${msg.sender === 'bot' ? 'bot-message' : 'user-message'}`}>
                    {msg.sender === 'bot' && (
                      <div className="bot-avatar">
                        <img src={Mina} alt="Mina" className="avatar-img" />
                      </div>
                    )}
                    <div className="message-bubble">
                      {renderMessageContent(msg.content)}
                    </div>
                  </div>
                ))}
              </div>
                  
              <div className="chat-input">
                <form onSubmit={(e) => { e.preventDefault(); handleProceedToAnswer(); }}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder="Ask Mina anything"
                    disabled={!hasPlayed || isTyping}
                  />
                  <button 
                    type="submit" 
                    className="send-message"
                    disabled={!hasPlayed || !userAnswer.trim() || isTyping}
                    aria-label="Send message"
                  >
                    <ArrowUp size={20} />
                  </button>
                </form>
              </div>
            </>
          )}
          
          {/* Mobile: EXACT COPY of AskMinaChat structure */}
          {isMobile && (
            <>
              <div className="context-chat-messages" ref={chatContentRef}>
                {messages.map((msg, index) => (
                  <div key={index} className={`context-chat-message ${msg.sender === 'bot' ? 'bot' : 'user'}`}>
                    {msg.sender === 'bot' && (
                      <div className="bot-avatar">
                        <img src={Mina} alt="Mina" className="avatar-logo" style={{ width: '24px', height: '24px' }} />
                      </div>
                    )}
                    <div className="message-content">
                      {renderMessageContent(msg.content)}
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="context-chat-message bot">
                    <div className="bot-avatar">
                      <img src={Mina} alt="Mina" className="avatar-logo" style={{ width: '24px', height: '24px' }} />
                    </div>
                    <div className="message-content">
                      <span className="typing-indicator">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="context-chat-input-container">
                <form onSubmit={(e) => { 
                  e.preventDefault(); 
                  handleProceedToAnswer();
                }}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder="Type your message..."
                    disabled={!hasPlayed || isTyping}
                  />
                  <button 
                    type="submit" 
                    className="send-message"
                    disabled={!hasPlayed || !userAnswer.trim() || isTyping}
                    aria-label="Send message"
                  >
                    <ArrowUp size={20} />
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Next button - positioned above Ask Mina input on mobile, bottom left on desktop */}
      {hasSubmitted && !showCompletion && (
        <button 
          className="listening-next-btn-bottom-left"
          onClick={handleNext}
        >
          {currentIndex < challenges.length - 1 ? 'Next Challenge' : 'Finish'}
        </button>
      )}
      
      
      
      {/* Exit confirmation modal */}
      {showExitModal && (
        <div className="listening-master-modal-overlay">
          <div className="listening-master-modal-content">
            <h3>Exit</h3>
            <p>Are you sure you want to exit?<br/>Your progress will be lost.</p>
            <div className="listening-master-modal-buttons">
              <button 
                className="listening-master-modal-cancel"
                onClick={() => setShowExitModal(false)}
              >
                Cancel
              </button>
              <button 
                className="listening-master-modal-confirm"
                onClick={() => navigate('/contests')}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Challenge Completion Screen */}
      {showCompletion && !isReplaying && (
        <div className="session-complete-wrapper">
          <div className="session-content">
            {animationData && (
              <div className="celebration-animation">
                <Lottie 
                  animationData={animationData} 
                  loop={false}
                  autoplay={true}
                  style={{ width: 400, height: 400 }}
                />
              </div>
            )}
            <h1 
              className="session-title" 
              style={{ 
                marginBottom: '0.5rem',
                fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem'
              }}
            >
              Challenge completed!
            </h1>
            <p 
              style={{ 
                fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem', 
                color: '#666', 
                marginBottom: window.innerWidth <= 768 ? '3rem' : '4rem', 
                marginTop: '0.5rem' 
              }}
            >
              Come back tomorrow for new content!
            </p>
            
            <button 
              className="view-results-button" 
              onClick={handleReplayChallenge}
              style={{
                fontSize: window.innerWidth <= 768 ? '0.95rem' : '1rem',
                padding: window.innerWidth <= 768 ? '14px 28px' : '16px 32px',
                width: window.innerWidth <= 768 ? 'auto' : 'auto',
                maxWidth: window.innerWidth <= 768 ? '200px' : '300px'
              }}
            >
              Replay Challenge
            </button>
            
            <p 
              onClick={handleGoToDashboard}
              style={{ 
                color: '#9334EA', 
                cursor: 'pointer', 
                marginTop: window.innerWidth <= 768 ? '1.5rem' : '2.5rem', 
                fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
                textDecoration: 'underline'
              }}
            >
              Back to Dashboard
            </p>
          </div>
        </div>
      )}

      {/* Replay Loading Screen */}
      {isReplaying && (
        <div className="session-complete-wrapper">
          <div className="session-content">
            <HiraganaLoader />
            <h1 className="session-title">Restarting challenge...</h1>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListeningChallenge;